import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowDownRight,
  ArrowUpRight,
  Calendar as CalendarDays,
  Calendar as CalendarIcon,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CreditCard,
  Download,
  Edit2,
  Eraser,
  FileDown,
  FileText,
  MoreHorizontal,
  Plus,
  ReceiptText,
  RotateCcw,
  Search,
  Trash2,
  Upload,
  Wallet,
} from "lucide-react";
import { addDays, addMonths, addWeeks, addYears, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFinanceStore } from "../stores/finance-store";
import { BankIcon } from "../components/BankIcon";
import { CurrencyInput } from "../components/CurrencyInput";
import { DataTable } from "../components/DataTable";
import { NumericMask } from "../components/NumericMask";
import { TransactionCombobox } from "../components/TransactionCombobox";
import { DetailedItemsEditor, createEmptyDetailItem } from "../components/DetailedItemsEditor";
import { RepeatTransactionButton, RepeatTransactionDialog } from "../components/RepeatTransactionDialog";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { CATEGORY_TYPES } from "../lib/transaction-types";
import { findCategoryById, findUniqueCategoryByName, formatTransactionCategoryName } from "../lib/category-relations";
import {
  buildCreditCardChargePayload,
  buildCreditCardInvoicePayload,
  getCardInvoiceCycle,
  getCardInvoiceCycleForDueMonth,
  getCardInvoiceOptions,
  getCreditCardCharges,
  getCreditCardInvoices,
  getInvoiceChargeAmount,
  getInvoiceCharges,
  getInvoiceTotal,
  getInvoiceTransaction,
  shiftCardInvoiceKey,
} from "../lib/credit-card";
import {
  cn,
  formatAmount,
  formatCategoryName,
  formatCurrency,
  formatDate,
  moneyToUnits,
  normalizeCategoryName,
  normalizeUppercase,
  parseCurrency,
  roundMoney,
  stringToColor,
} from "../lib/utils";

const CARD_BRANDS = [
  { value: "visa", label: "Visa", color: "bg-blue-600" },
  { value: "mastercard", label: "Mastercard", color: "bg-orange-500" },
  { value: "elo", label: "Elo", color: "bg-yellow-500" },
  { value: "amex", label: "American Express", color: "bg-blue-400" },
  { value: "hipercard", label: "Hipercard", color: "bg-red-600" },
  { value: "other", label: "Outro", color: "bg-gray-500" },
];

const createDefaultCardForm = () => ({
  name: "",
  brand: "visa",
  lastDigits: "",
  limit: "",
  closingDay: "19",
  dueDay: "25",
  accountId: "",
});

const DEFAULT_REPEAT_SETTINGS = {
  recurrenceType: "none",
  recurrenceFrequency: "monthly",
  recurrenceInterval: "1",
  installmentCount: "2",
  installmentStartIndex: "1",
  amountMode: "per_installment",
  fixedTotalEnabled: false,
  fixedTotalCount: "12",
};

const createDefaultExpenseForm = () => ({
  cardChargeMode: "expense",
  description: "",
  amount: "",
  category: "",
  categoryId: null,
  details: [],
  notes: "",
  amountSign: "-",
  date: new Date(),
  invoiceKey: "",
  invoiceManual: false,
  ...DEFAULT_REPEAT_SETTINGS,
});

function toPositiveInt(value, fallback, min = 1) {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(min, parsed);
}

function addByFrequency(date, frequency, amount) {
  if (frequency === "daily") return addDays(date, amount);
  if (frequency === "weekly") return addWeeks(date, amount);
  if (frequency === "yearly") return addYears(date, amount);
  return addMonths(date, amount);
}

function calculateOccurrencesUntilDecember(startDate, frequency, interval) {
  const endDate = new Date(startDate.getFullYear(), 11, 31, 23, 59, 59, 999);
  let count = 0;
  let nextDate = startDate;

  while (nextDate <= endDate && count < 366) {
    count += 1;
    nextDate = addByFrequency(startDate, frequency, count * interval);
  }

  return Math.max(1, count);
}

function appendInstallmentSuffix(description, installmentIndex, totalInstallments) {
  if (!installmentIndex || !totalInstallments || totalInstallments <= 1) return description;
  return `${description} (${installmentIndex}/${totalInstallments})`;
}

function normalizeChargeDetails(details = []) {
  return Array.isArray(details)
    ? details.map((item) => ({
        id: item.id || crypto.randomUUID(),
        description: normalizeCategoryName(item.description),
        amount: item.amount || "",
      }))
    : [];
}

function validateChargeDetails(items, targetAmount) {
  const targetUnits = moneyToUnits(targetAmount);
  const filled = items.filter((item) => item.description?.trim() || moneyToUnits(item.amount) > 0);

  if (!filled.length) {
    return { success: false, message: "Inclua ao menos um detalhe." };
  }

  if (filled.some((item) => !item.description?.trim())) {
    return { success: false, message: "Todos os detalhes precisam de descrição." };
  }

  if (filled.some((item) => moneyToUnits(item.amount) <= 0)) {
    return { success: false, message: "Todos os detalhes precisam de valor maior que zero." };
  }

  const detailsUnits = filled.reduce((sum, item) => sum + moneyToUnits(item.amount), 0);
  if (detailsUnits !== targetUnits) {
    return { success: false, message: "A soma dos detalhes deve ser exatamente igual ao Valor Total." };
  }

  return {
    success: true,
    details: filled.map((item) => ({
      id: item.id,
      description: normalizeCategoryName(item.description),
      amount: roundMoney(parseCurrency(item.amount)),
    })),
  };
}

function cleanChargeDescription(description) {
  return String(description || "Sem descrição").replace(/\s*\(\d+\/\d+\)$/, "");
}

function getChargeInstallmentLabel(charge = {}) {
  const descriptionMatch = String(charge.description || "").match(/\((\d+\/\d+)\)$/);
  if (descriptionMatch) return descriptionMatch[1];
  if (charge.installment_index && charge.total_installments) {
    return `${charge.installment_index}/${charge.total_installments}`;
  }
  return "1";
}

function getChargeRecurrenceLabel(charge = {}) {
  if (charge.card_charge_mode === "detailed" || normalizeChargeDetails(charge.details).length) return "Detalhado";
  if (charge.recurrence_type === "fixed") return "Fixa";
  if (charge.recurrence_type === "installment" || charge.recurrence_id) return "Parcelado";
  return null;
}

function ChargeRecurrenceBadge({ charge }) {
  const label = getChargeRecurrenceLabel(charge);
  if (!label) return null;

  return (
    <span className="inline-flex w-fit shrink-0 items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
  );
}

function getBrandInfo(brandValue) {
  return CARD_BRANDS.find((brand) => brand.value === brandValue) || CARD_BRANDS[CARD_BRANDS.length - 1];
}

function normalizeImportText(value) {
  return normalizeCategoryName(value).replace(/[^A-Z0-9]/g, "");
}

function getImportDateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function isProbableImportedDuplicate(item, charges = []) {
  const itemDate = getImportDateKey(`${item.date}T12:00:00`);
  const itemAmount = roundMoney(parseCurrency(item.amount));
  const itemDescription = normalizeImportText(item.description);
  const itemSign = item.amount_sign || "-";

  return charges.some((charge) => {
    const chargeDate = getImportDateKey(charge.date);
    const chargeAmount = roundMoney(parseCurrency(charge.amount));
    const chargeDescription = normalizeImportText(charge.description);
    const chargeSign = charge.amount_sign || "-";

    return chargeDate === itemDate &&
      chargeAmount === itemAmount &&
      chargeSign === itemSign &&
      (
        chargeDescription === itemDescription ||
        chargeDescription.includes(itemDescription) ||
        itemDescription.includes(chargeDescription)
      );
  });
}

function getImportedInvoiceAmount(item = {}) {
  const amount = Math.abs(parseCurrency(item.amount));
  return item.amount_sign === "+" ? -amount : amount;
}

function getInvoiceAmountLabel(value, amountSign = "-") {
  const prefix = amountSign === "+" ? "+" : "-";
  return `${prefix} ${formatAmount(Math.abs(parseCurrency(value)), 2)}`;
}

function getInvoiceAmountClass(amountSign = "-") {
  return amountSign === "+" ? "text-emerald-600" : "text-destructive";
}

function getCardChargeModeSign(mode = "expense") {
  return mode === "income" ? "+" : "-";
}

function sanitizeFilename(value = "arquivo") {
  return normalizeCategoryName(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "arquivo";
}

function escapeCsvValue(value) {
  const text = String(value ?? "");
  return /[",\n\r;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getInvoiceExportRows(charges = [], categories = []) {
  return charges.map((charge) => ({
    date: formatDate(charge.date),
    description: cleanChargeDescription(charge.description),
    category: formatTransactionCategoryName(charge, categories),
    type: charge.amount_sign === "+" ? "Receita" : "Despesa",
    amount: getInvoiceAmountLabel(charge.amount, charge.amount_sign),
    installment: getChargeInstallmentLabel(charge),
  }));
}

function downloadTextFile(filename, content, type = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportInvoiceCsv({ card, monthLabel, charges, categories }) {
  const rows = getInvoiceExportRows(charges, categories);
  const header = ["Data", "Descricao", "Categoria", "Tipo", "Valor", "Qtd"];
  const csv = [
    header.join(";"),
    ...rows.map((row) => [
      row.date,
      row.description,
      row.category,
      row.type,
      row.amount,
      row.installment,
    ].map(escapeCsvValue).join(";")),
  ].join("\r\n");

  const filename = `fatura-${sanitizeFilename(card?.name)}-${sanitizeFilename(monthLabel)}.csv`;
  downloadTextFile(filename, `\ufeff${csv}`, "text/csv;charset=utf-8");
}

function exportInvoicePdf({ card, account, monthLabel, invoiceTotal, charges, categories }) {
  const rows = getInvoiceExportRows(charges, categories);
  const title = `Fatura ${card?.name || ""} - ${monthLabel || ""}`;
  const popup = window.open("", "_blank");

  if (!popup) {
    toast.error("Não foi possível abrir a janela de impressão do PDF.");
    return;
  }

  popup.document.write(`<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111827; margin: 32px; }
    h1 { font-size: 22px; margin: 0 0 6px; }
    .meta { color: #4b5563; margin-bottom: 18px; }
    .total { font-size: 18px; font-weight: 700; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { text-align: left; border-bottom: 2px solid #d1d5db; padding: 8px 6px; }
    td { border-bottom: 1px solid #e5e7eb; padding: 8px 6px; vertical-align: top; }
    th.amount, td.amount { text-align: right; white-space: nowrap; }
    .credit { color: #059669; }
    .debit { color: #dc2626; }
    @media print { body { margin: 18mm; } }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">${escapeHtml(account?.bank || account?.name || "Conta vinculada")} • ${escapeHtml(card?.brand || "")}</div>
  <div class="total">Valor da fatura: ${escapeHtml(formatCurrency(invoiceTotal))}</div>
  <table>
    <thead>
      <tr>
        <th>Data</th>
        <th>Descrição</th>
        <th>Categoria</th>
        <th>Tipo</th>
        <th class="amount">Valor</th>
        <th>Qtd</th>
      </tr>
    </thead>
    <tbody>
      ${rows.map((row) => `
        <tr>
          <td>${escapeHtml(row.date)}</td>
          <td>${escapeHtml(row.description)}</td>
          <td>${escapeHtml(row.category)}</td>
          <td>${escapeHtml(row.type)}</td>
          <td class="amount ${row.type === "Receita" ? "credit" : "debit"}">${escapeHtml(row.amount)}</td>
          <td>${escapeHtml(row.installment)}</td>
        </tr>
      `).join("")}
    </tbody>
  </table>
  <script>
    window.addEventListener("load", () => {
      window.focus();
      window.print();
    });
  </script>
</body>
</html>`);
  popup.document.close();
}

export default function CardsPage() {
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showClearInvoiceDialog, setShowClearInvoiceDialog] = useState(false);
  const [editCardId, setEditCardId] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [deleteCardId, setDeleteCardId] = useState(null);
  const [deleteExpense, setDeleteExpense] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [invoiceMonth, setInvoiceMonth] = useState(new Date().getMonth());
  const [invoiceYear, setInvoiceYear] = useState(new Date().getFullYear());
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [invoiceCategoryFilter, setInvoiceCategoryFilter] = useState("all");
  const [cardForm, setCardForm] = useState(createDefaultCardForm);
  const [expenseForm, setExpenseForm] = useState(createDefaultExpenseForm);

  const {
    cards,
    accounts,
    transactions,
    categories,
    fetchCards,
    fetchAccounts,
    fetchTransactions,
    fetchCategories,
    addCard,
    updateCard,
    deleteCard,
    addTransaction,
    addTransactionBatch,
    updateTransaction,
    deleteTransaction,
    addCategory,
    parseCardInvoicePdf,
    isLoading,
  } = useFinanceStore();

  useEffect(() => {
    fetchCards();
    fetchAccounts();
    fetchTransactions();
    fetchCategories();
  }, [fetchCards, fetchAccounts, fetchTransactions, fetchCategories]);

  useEffect(() => {
    if (!cards.length) {
      setSelectedCardId(null);
      setViewMode("cards");
      return;
    }

    if (!selectedCardId || !cards.some((card) => card.id === selectedCardId)) {
      setSelectedCardId(cards[0].id);
    }
  }, [cards, selectedCardId]);

  const selectedCard = cards.find((card) => card.id === selectedCardId) || null;

  const resolveCardAccount = (card) => {
    if (!card) return null;
    return accounts.find((account) => account.id === card.accountId) ||
      accounts.find((account) => account.name === card.accountName) ||
      null;
  };

  const getChargesForSync = (card, invoiceKey, options = {}) => {
    const removeIds = new Set([
      ...(options.removeIds || []),
      options.removeId,
    ].filter(Boolean));
    const replaceCharges = new Map(
      [
        ...(options.replaceCharges || []),
        options.replaceCharge,
      ]
        .filter(Boolean)
        .map((item) => [item.id, item.data])
    );
    const addCharges = [
      ...(options.addCharges || []),
      options.addCharge,
    ].filter(Boolean);

    const charges = getCreditCardCharges(transactions)
      .filter((tx) => tx.card_id === card.id)
      .map((tx) => {
        if (replaceCharges.has(tx.id)) {
          return { ...tx, ...replaceCharges.get(tx.id) };
        }
        return tx;
      })
      .filter((tx) => !removeIds.has(tx.id))
      .filter((tx) => tx.invoice_key === invoiceKey);

    charges.push(...addCharges.filter((charge) => charge.invoice_key === invoiceKey));

    return charges;
  };

  const syncInvoiceForKey = async (card, invoiceKey, options = {}) => {
    const account = resolveCardAccount(card);
    if (!card || !account || !invoiceKey) return { success: false };

    const [, monthKey] = invoiceKey.split(":");
    const [yearValue, monthValue] = monthKey.split("-").map((value) => parseInt(value, 10));
    const cycle = getCardInvoiceCycleForDueMonth(card, yearValue, monthValue - 1);
    const charges = getChargesForSync(card, invoiceKey, options);
    const total = getInvoiceTotal(charges);
    const existingInvoice = getInvoiceTransaction(transactions, card, invoiceKey);

    if (total <= 0) {
      if (existingInvoice?.id) {
        return deleteTransaction(existingInvoice.id);
      }
      return { success: true };
    }

    const invoicePayload = buildCreditCardInvoicePayload({
      card,
      account,
      cycle,
      total,
      existingInvoice,
    });

    if (existingInvoice?.id) {
      return updateTransaction(existingInvoice.id, "expense", invoicePayload);
    }

    return addTransaction("expense", invoicePayload);
  };

  const syncInvoiceKeys = async (card, invoiceKeys, options = {}) => {
    const uniqueInvoiceKeys = [...new Set(invoiceKeys.filter(Boolean))];
    for (const invoiceKey of uniqueInvoiceKeys) {
      await syncInvoiceForKey(card, invoiceKey, options);
    }
  };

  const syncExistingInvoicesAfterCardUpdate = async (card, account) => {
    const invoices = getCreditCardInvoices(transactions).filter((tx) => tx.card_id === card.id);

    for (const invoice of invoices) {
      const [, monthKey] = String(invoice.invoice_key || "").split(":");
      if (!monthKey) continue;

      const [yearValue, monthValue] = monthKey.split("-").map((value) => parseInt(value, 10));
      const cycle = getCardInvoiceCycleForDueMonth(card, yearValue, monthValue - 1);
      const payload = buildCreditCardInvoicePayload({
        card,
        account,
        cycle,
        total: parseCurrency(invoice.amount),
        existingInvoice: invoice,
      });

      await updateTransaction(invoice.id, "expense", payload);
    }
  };

  const openCreateCardDialog = () => {
    setEditCardId(null);
    setCardForm(createDefaultCardForm());
    setShowCardDialog(true);
  };

  const openEditCardDialog = (card) => {
    setEditCardId(card.id);
    setCardForm({
      name: card.name || "",
      brand: card.brand || "visa",
      lastDigits: card.lastDigits || "",
      limit: String(card.limit || ""),
      closingDay: String(card.closingDay || "19"),
      dueDay: String(card.dueDay || "25"),
      accountId: card.accountId || resolveCardAccount(card)?.id || "",
    });
    setShowCardDialog(true);
  };

  const handleCardSubmit = async (event) => {
    event.preventDefault();
    const linkedAccount = accounts.find((account) => account.id === cardForm.accountId);

    if (!linkedAccount) {
      toast.error("Vincule o cartão a uma conta corrente.");
      return;
    }

    const payload = {
      name: cardForm.name.trim(),
      brand: cardForm.brand,
      lastDigits: cardForm.lastDigits,
      limit: parseCurrency(cardForm.limit),
      closingDay: parseInt(cardForm.closingDay, 10) || 1,
      dueDay: parseInt(cardForm.dueDay, 10) || 10,
      accountId: linkedAccount.id,
      accountName: linkedAccount.name,
    };

    if (!payload.name) {
      toast.error("Informe o nome do cartão.");
      return;
    }

    const result = editCardId
      ? await updateCard(editCardId, payload)
      : await addCard(payload);

    if (result.success) {
      if (editCardId) {
        await syncExistingInvoicesAfterCardUpdate({ ...payload, id: editCardId }, linkedAccount);
      }

      toast.success(editCardId ? "Cartão atualizado!" : "Cartão adicionado!");
      setCardForm(createDefaultCardForm());
      setEditCardId(null);
      setShowCardDialog(false);
    } else {
      toast.error(editCardId ? "Erro ao atualizar cartão" : "Erro ao adicionar cartão");
    }
  };

  const handleDeleteCard = async () => {
    if (!deleteCardId) return;

    const result = await deleteCard(deleteCardId);
    if (result.success) {
      toast.success("Cartão excluído!");
    } else {
      toast.error("Erro ao excluir cartão");
    }
    setDeleteCardId(null);
  };

  const openCreateExpenseDialog = (overrides = {}) => {
    if (!selectedCard) return;
    if (!resolveCardAccount(selectedCard)) {
      toast.error("Vincule uma conta ao cartão antes de lançar movimentações.");
      openEditCardDialog(selectedCard);
      return;
    }

    const defaults = overrides?.nativeEvent ? {} : overrides;
    setEditExpense(null);
    const date = defaults.date || new Date();
    const invoiceKey = defaults.invoiceKey || selectedCycle?.invoiceKey || getCardInvoiceCycle(selectedCard, date).invoiceKey;
    setExpenseForm({
      ...createDefaultExpenseForm(),
      ...defaults,
      date,
      invoiceKey,
      invoiceManual: defaults.invoiceManual ?? Boolean(defaults.invoiceKey || selectedCycle?.invoiceKey),
    });
    setShowExpenseDialog(true);
  };

  const openEditExpenseDialog = (charge) => {
    const date = new Date(charge.date || new Date());
    const automaticInvoiceKey = selectedCard ? getCardInvoiceCycle(selectedCard, date).invoiceKey : charge.invoice_key;
    const editChargeMode = charge.card_charge_mode === "detailed" || normalizeChargeDetails(charge.details).length
      ? "detailed"
      : charge.amount_sign === "+"
        ? "income"
        : "expense";
    setEditExpense(charge);
    setExpenseForm({
      cardChargeMode: editChargeMode,
      description: normalizeUppercase(charge.description),
      amount: String(charge.amount || ""),
      category: charge.category || "",
      categoryId: charge.category_id || null,
      details: normalizeChargeDetails(charge.details),
      notes: charge.notes || "",
      amountSign: getCardChargeModeSign(editChargeMode),
      date,
      invoiceKey: charge.invoice_key || automaticInvoiceKey || "",
      invoiceManual: Boolean(charge.manual_invoice_key || (charge.invoice_key && charge.invoice_key !== automaticInvoiceKey)),
      recurrenceType: charge.recurrence_type || "none",
      recurrenceFrequency: charge.recurrence_frequency || DEFAULT_REPEAT_SETTINGS.recurrenceFrequency,
      recurrenceInterval: String(charge.recurrence_interval || DEFAULT_REPEAT_SETTINGS.recurrenceInterval),
      installmentCount: String(charge.total_installments || DEFAULT_REPEAT_SETTINGS.installmentCount),
      installmentStartIndex: String(charge.installment_index || charge.installment_start_index || DEFAULT_REPEAT_SETTINGS.installmentStartIndex),
      amountMode: charge.amount_mode || DEFAULT_REPEAT_SETTINGS.amountMode,
      fixedTotalEnabled: Boolean(charge.fixed_total_occurrences),
      fixedTotalCount: String(charge.fixed_total_occurrences || DEFAULT_REPEAT_SETTINGS.fixedTotalCount),
    });
    setShowExpenseDialog(true);
  };

  const ensureCardChargeCategory = async (categoryName, categoryId = null, categoryType = CATEGORY_TYPES.EXPENSE) => {
    const normalized = normalizeCategoryName(categoryName);
    if (!normalized) return null;
    const scopedCategories = categories.filter((category) => category.type === categoryType);

    const byId = findCategoryById(scopedCategories, categoryId, categoryType);
    if (byId) return byId;

    const unique = findUniqueCategoryByName(scopedCategories, normalized, categoryType);
    if (unique) return unique;

    const exists = scopedCategories.some((category) => normalizeCategoryName(category.name) === normalized);
    if (exists) return null;

    const result = await addCategory(categoryType, {
      name: normalized,
      color: stringToColor(normalized),
    });
    return result?.success === false ? false : result?.category || null;
  };

  const buildCreditCardChargeOccurrences = (card, account, form) => {
    const totalAmount = parseCurrency(form.amount);
    const recurrenceType = form.recurrenceType || "none";
    const frequency = form.recurrenceFrequency || DEFAULT_REPEAT_SETTINGS.recurrenceFrequency;
    const interval = toPositiveInt(form.recurrenceInterval, 1);
    let count = 1;
    let totalInstallments = 1;
    let startIndex = 1;
    let amountPerOccurrence = totalAmount;

    if (recurrenceType === "installment") {
      totalInstallments = toPositiveInt(form.installmentCount, 2, 2);
      startIndex = Math.min(toPositiveInt(form.installmentStartIndex, 1), totalInstallments);
      count = totalInstallments - startIndex + 1;
      amountPerOccurrence = form.amountMode === "total" ? totalAmount / totalInstallments : totalAmount;
    } else if (recurrenceType === "fixed") {
      count = form.fixedTotalEnabled
        ? toPositiveInt(form.fixedTotalCount, 1)
        : calculateOccurrencesUntilDecember(form.date, frequency, interval);
      totalInstallments = count;
    }

    const recurrenceId = recurrenceType !== "none" ? crypto.randomUUID() : null;
    const baseDescription = normalizeUppercase(form.description);

    return Array.from({ length: count }, (_, index) => {
      const occurrenceDate = recurrenceType === "none"
        ? form.date
        : addByFrequency(form.date, frequency, index * interval);
      const installmentIndex = recurrenceType === "installment" ? startIndex + index : index + 1;
      const invoiceKey = recurrenceType === "installment"
        ? shiftCardInvoiceKey(card, form.invoiceKey, index)
        : index === 0
          ? form.invoiceKey
          : getCardInvoiceCycle(card, occurrenceDate).invoiceKey;
      const recurrenceData = recurrenceType === "none"
        ? {}
        : {
            recurrence_id: recurrenceId,
            recurrence_type: recurrenceType,
            recurrence_frequency: frequency,
            recurrence_interval: interval,
            installment_index: installmentIndex,
            total_installments: totalInstallments,
            amount_mode: recurrenceType === "installment" ? form.amountMode : "per_occurrence",
            installment_start_index: recurrenceType === "installment" ? startIndex : undefined,
            fixed_total_occurrences: recurrenceType === "fixed" && form.fixedTotalEnabled ? count : undefined,
          };
      const description = recurrenceType === "installment"
        ? appendInstallmentSuffix(baseDescription, installmentIndex, totalInstallments)
        : baseDescription;

      return buildCreditCardChargePayload({
        card,
        account,
        data: {
          ...form,
          description,
          amount: roundMoney(amountPerOccurrence),
          date: occurrenceDate,
          invoiceKey,
          invoiceManual: index === 0 ? form.invoiceManual : recurrenceType === "installment",
          recurrenceData,
        },
      });
    });
  };

  const handleExpenseSubmit = async (event) => {
    event.preventDefault();
    if (!selectedCard) return;

    const account = resolveCardAccount(selectedCard);
    if (!account) {
      toast.error("Vincule uma conta ao cartão antes de lançar movimentações.");
      openEditCardDialog(selectedCard);
      return;
    }

    const amount = parseCurrency(expenseForm.amount);
    if (!expenseForm.description.trim()) {
      toast.error("Informe a descrição do lançamento.");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    if (!expenseForm.date || Number.isNaN(new Date(expenseForm.date).getTime())) {
      toast.error("Informe uma data válida.");
      return;
    }
    if (expenseForm.cardChargeMode === "detailed" && expenseForm.recurrenceType !== "none") {
      toast.error("Lançamento detalhado de cartão deve ser lançado como ocorrência única.");
      return;
    }

    const detailValidation = expenseForm.cardChargeMode === "detailed"
      ? validateChargeDetails(expenseForm.details, amount)
      : { success: true, details: [] };
    if (!detailValidation.success) {
      toast.error(detailValidation.message);
      return;
    }

    const categoryType = expenseForm.cardChargeMode === "income" ? CATEGORY_TYPES.INCOME : CATEGORY_TYPES.EXPENSE;
    const selectedCategory = await ensureCardChargeCategory(expenseForm.category, expenseForm.categoryId, categoryType);
    if (selectedCategory === false) {
      toast.error("Erro ao adicionar categoria.");
      return;
    }

    const normalizedForm = {
      ...expenseForm,
      description: normalizeUppercase(expenseForm.description),
      category: normalizeCategoryName(selectedCategory?.name || expenseForm.category),
      categoryId: selectedCategory?.id || null,
      cardChargeMode: expenseForm.cardChargeMode,
      details: detailValidation.details,
      notes: expenseForm.cardChargeMode === "detailed" ? expenseForm.notes || "" : "",
      amountSign: getCardChargeModeSign(expenseForm.cardChargeMode),
      date: new Date(expenseForm.date),
      invoiceKey: expenseForm.invoiceKey || getCardInvoiceCycle(selectedCard, expenseForm.date).invoiceKey,
    };
    const chargePayloads = buildCreditCardChargeOccurrences(selectedCard, account, normalizedForm);
    const affectedInvoiceKeys = chargePayloads.map((charge) => charge.invoice_key);

    if (editExpense?.id) {
      const oldInvoiceKey = editExpense.invoice_key;
      const chargePayload = chargePayloads[0];
      const result = await updateTransaction(editExpense.id, "expense", {
        ...editExpense,
        ...chargePayload,
      });

      if (result.success) {
        if (oldInvoiceKey && oldInvoiceKey !== chargePayload.invoice_key) {
          await syncInvoiceForKey(selectedCard, oldInvoiceKey, { removeId: editExpense.id });
          await syncInvoiceForKey(selectedCard, chargePayload.invoice_key, { addCharge: chargePayload });
        } else {
          await syncInvoiceForKey(selectedCard, chargePayload.invoice_key, {
            replaceCharge: { id: editExpense.id, data: chargePayload },
          });
        }

        toast.success("Lançamento atualizado!");
        setShowExpenseDialog(false);
        setEditExpense(null);
        setExpenseForm(createDefaultExpenseForm());
      } else {
        toast.error("Erro ao atualizar lançamento.");
      }
      return;
    }

    const result = chargePayloads.length === 1
      ? await addTransaction("expense", chargePayloads[0])
      : await addTransactionBatch(chargePayloads.map((payload) => ({ type: "expense", data: payload })));
    if (result.success) {
      await syncInvoiceKeys(selectedCard, affectedInvoiceKeys, { addCharges: chargePayloads });
      toast.success(chargePayloads.length === 1 ? "Lançamento adicionado à fatura!" : `${chargePayloads.length} lançamentos adicionados às faturas!`);
      setShowExpenseDialog(false);
      setExpenseForm(createDefaultExpenseForm());
    } else {
      toast.error("Erro ao adicionar lançamento.");
    }
  };

  const handleImportCharges = async (items) => {
    if (!selectedCard || !selectedCycle) return { success: false };

    const account = resolveCardAccount(selectedCard);
    if (!account) {
      toast.error("Vincule uma conta ao cartão antes de importar faturas.");
      openEditCardDialog(selectedCard);
      return { success: false };
    }

    const chargePayloads = items.map((item) => buildCreditCardChargePayload({
      card: selectedCard,
      account,
      data: {
        description: normalizeUppercase(item.description),
        amount: item.amount,
        amountSign: item.amount_sign || "-",
        category: "",
        categoryId: null,
        date: new Date(`${item.date}T12:00:00`),
        invoiceKey: selectedCycle.invoiceKey,
        invoiceManual: true,
      },
    }));

    const result = await addTransactionBatch(chargePayloads.map((payload) => ({ type: "expense", data: payload })));
    if (result.success) {
      await syncInvoiceKeys(selectedCard, [selectedCycle.invoiceKey], { addCharges: chargePayloads });
      toast.success(`${chargePayloads.length} despesa${chargePayloads.length > 1 ? "s" : ""} importada${chargePayloads.length > 1 ? "s" : ""}!`);
      return { success: true };
    }

    toast.error("Erro ao importar despesas.");
    return { success: false };
  };

  const handleDeleteExpense = async () => {
    if (!deleteExpense || !selectedCard) return;

    const invoiceKey = deleteExpense.invoice_key;
    const result = await deleteTransaction(deleteExpense.id);
    if (result.success) {
      await syncInvoiceForKey(selectedCard, invoiceKey, { removeId: deleteExpense.id });
      toast.success("Lançamento excluído!");
      setDeleteExpense(null);
      setShowExpenseDialog(false);
      setEditExpense(null);
    } else {
      toast.error("Erro ao excluir lançamento.");
    }
  };

  const handlePayInvoice = async (invoice) => {
    if (!invoice?.id) return;
    const result = await updateTransaction(invoice.id, "expense", {
      ...invoice,
      status: true,
      confirmed: true,
      realized_at: invoice.date,
    });

    if (result.success) {
      toast.success("Fatura paga!");
    } else {
      toast.error("Erro ao pagar fatura.");
    }
  };

  const openChargebackDialog = () => {
    openCreateExpenseDialog({
      cardChargeMode: "income",
      amountSign: "+",
      description: "EXTORNO",
      category: "",
      categoryId: null,
      invoiceKey: selectedCycle?.invoiceKey || "",
      invoiceManual: true,
    });
  };

  const handleExportInvoiceCsv = () => {
    if (!selectedCard || !selectedCharges.length) {
      toast.error("Não há lançamentos para exportar.");
      return;
    }

    exportInvoiceCsv({
      card: selectedCard,
      monthLabel: selectedMonthLabel,
      charges: selectedCharges,
      categories,
    });
    toast.success("CSV da fatura gerado.");
  };

  const handleExportInvoicePdf = () => {
    if (!selectedCard || !selectedCharges.length) {
      toast.error("Não há lançamentos para exportar.");
      return;
    }

    exportInvoicePdf({
      card: selectedCard,
      account: selectedAccount,
      monthLabel: selectedMonthLabel,
      invoiceTotal: selectedInvoiceTotal,
      charges: selectedCharges,
      categories,
    });
  };

  const handleClearInvoice = async () => {
    if (!selectedCard || !selectedCycle) return;

    const chargeIds = selectedCharges.map((charge) => charge.id).filter(Boolean);
    if (!chargeIds.length) {
      toast.error("Não há lançamentos para limpar nesta fatura.");
      setShowClearInvoiceDialog(false);
      return;
    }

    const deletedIds = [];
    for (const id of chargeIds) {
      const result = await deleteTransaction(id);
      if (result.success) {
        deletedIds.push(id);
      }
    }

    if (deletedIds.length) {
      await syncInvoiceForKey(selectedCard, selectedCycle.invoiceKey, { removeIds: deletedIds });
    }

    setShowClearInvoiceDialog(false);

    if (deletedIds.length === chargeIds.length) {
      toast.success("Fatura limpa.");
    } else {
      toast.error("Alguns lançamentos não puderam ser removidos.");
    }
  };

  const handlePrevInvoiceMonth = () => {
    if (invoiceMonth === 0) {
      setInvoiceMonth(11);
      setInvoiceYear((year) => year - 1);
    } else {
      setInvoiceMonth((month) => month - 1);
    }
  };

  const handleNextInvoiceMonth = () => {
    if (invoiceMonth === 11) {
      setInvoiceMonth(0);
      setInvoiceYear((year) => year + 1);
    } else {
      setInvoiceMonth((month) => month + 1);
    }
  };

  const selectedCycle = useMemo(() => (
    selectedCard
      ? getCardInvoiceCycleForDueMonth(selectedCard, invoiceYear, invoiceMonth)
      : null
  ), [invoiceMonth, invoiceYear, selectedCard]);
  const selectedCharges = useMemo(() => (
    selectedCard && selectedCycle
      ? getInvoiceCharges(transactions, selectedCard, selectedCycle.invoiceKey)
      : []
  ), [selectedCard, selectedCycle, transactions]);
  const selectedInvoice = useMemo(() => (
    selectedCard && selectedCycle
      ? getInvoiceTransaction(transactions, selectedCard, selectedCycle.invoiceKey)
      : null
  ), [selectedCard, selectedCycle, transactions]);
  const selectedInvoiceTotal = selectedInvoice
    ? parseCurrency(selectedInvoice.amount)
    : getInvoiceTotal(selectedCharges);
  const selectedAccount = resolveCardAccount(selectedCard);
  const selectedMonthLabel = new Date(invoiceYear, invoiceMonth, 1).toLocaleString("pt-BR", {
    month: "long",
    year: "numeric",
  });
  const invoiceCategoryOptions = useMemo(() => {
    const options = new Map();
    selectedCharges.forEach((charge) => {
      const normalized = normalizeCategoryName(charge.category);
      if (normalized) {
        options.set(normalized, formatCategoryName(normalized));
      }
    });

    return Array.from(options, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [selectedCharges]);
  const filteredInvoiceCharges = useMemo(() => {
    const search = normalizeCategoryName(invoiceSearchTerm);

    return selectedCharges
      .filter((charge) => {
        const category = normalizeCategoryName(charge.category);
        if (invoiceCategoryFilter !== "all" && category !== invoiceCategoryFilter) {
          return false;
        }

        if (!search) return true;

        return [
          charge.description,
          charge.category,
          selectedCard?.name,
        ].some((value) => normalizeCategoryName(value).includes(search));
      })
      .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  }, [invoiceCategoryFilter, invoiceSearchTerm, selectedCard?.name, selectedCharges]);

  useEffect(() => {
    if (
      invoiceCategoryFilter !== "all" &&
      !invoiceCategoryOptions.some((option) => option.value === invoiceCategoryFilter)
    ) {
      setInvoiceCategoryFilter("all");
    }
  }, [invoiceCategoryFilter, invoiceCategoryOptions]);

  const handleSelectCard = (card) => {
    setSelectedCardId(card.id);
    setViewMode("invoice");
  };

  const handleBackToCards = () => {
    setViewMode("cards");
    setInvoiceSearchTerm("");
    setInvoiceCategoryFilter("all");
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {viewMode === "cards" ? (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Cartões de crédito</h1>
              <p className="text-muted-foreground mt-1">Gerencie cartões, faturas e lançamentos vinculados</p>
            </div>

            <Button onClick={openCreateCardDialog} className="btn-glow hidden sm:inline-flex" data-testid="add-card-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Cartão
            </Button>
          </div>

          <section className="space-y-4">
            {isLoading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {[1, 2, 3].map((item) => (
                  <Skeleton key={item} className="h-64" />
                ))}
              </div>
            ) : cards.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {cards.map((card) => (
                  <CreditCardTile
                    key={card.id}
                    card={card}
                    account={resolveCardAccount(card)}
                    transactions={transactions}
                    isSelected={viewMode === "invoice" && card.id === selectedCardId}
                    onSelect={() => handleSelectCard(card)}
                    onEdit={() => openEditCardDialog(card)}
                    onDelete={() => setDeleteCardId(card.id)}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="empty-state py-12">
                  <CreditCard className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Nenhum cartão cadastrado</p>
                  <Button onClick={openCreateCardDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Cartão
                  </Button>
                </CardContent>
              </Card>
            )}
          </section>

          <Button
            onClick={openCreateCardDialog}
            className="btn-glow fixed bottom-20 left-1/2 z-40 h-12 w-[220px] -translate-x-1/2 rounded-full px-5 shadow-lg sm:hidden"
            data-testid="add-card-btn-mobile"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Cartão
          </Button>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <Button variant="ghost" size="icon" className="mt-1 h-9 w-9 shrink-0" onClick={handleBackToCards} aria-label="Voltar para cartões">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Cartões de crédito</h1>
                <p className="mt-1 truncate text-muted-foreground">
                  {selectedCard
                    ? `Fatura de ${selectedCard.name}${selectedAccount ? ` • ${selectedAccount.bank || selectedAccount.name}` : ""}`
                    : "Selecione um cartão para ver a fatura"}
                </p>
              </div>
            </div>

            <Button className="btn-glow hidden sm:inline-flex" onClick={openCreateExpenseDialog} disabled={!selectedAccount}>
              <Plus className="mr-2 h-4 w-4" />
              Despesa
            </Button>
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_350px]">
            <InvoiceChargesView
              card={selectedCard}
              charges={filteredInvoiceCharges}
              expenseCategories={categories}
              isLoading={isLoading}
              monthLabel={selectedMonthLabel}
              searchTerm={invoiceSearchTerm}
              onSearchChange={setInvoiceSearchTerm}
              categoryFilter={invoiceCategoryFilter}
              onCategoryFilterChange={setInvoiceCategoryFilter}
              categoryOptions={invoiceCategoryOptions}
              onPrevMonth={handlePrevInvoiceMonth}
              onNextMonth={handleNextInvoiceMonth}
              onEditExpense={openEditExpenseDialog}
              onDeleteExpense={setDeleteExpense}
            />

            <InvoicePanel
              card={selectedCard}
              account={selectedAccount}
              cycle={selectedCycle}
              invoice={selectedInvoice}
              invoiceTotal={selectedInvoiceTotal}
              chargeCount={selectedCharges.length}
              monthLabel={selectedMonthLabel}
              onAddExpense={openCreateExpenseDialog}
              onImportInvoice={() => setShowImportDialog(true)}
              onEditCard={() => selectedCard && openEditCardDialog(selectedCard)}
              onPayInvoice={handlePayInvoice}
              onClearInvoice={() => setShowClearInvoiceDialog(true)}
              onExportCsv={handleExportInvoiceCsv}
              onExportPdf={handleExportInvoicePdf}
              onCreateChargeback={openChargebackDialog}
            />
          </div>
        </>
      )}

      <CardDialog
        open={showCardDialog}
        onOpenChange={setShowCardDialog}
        formData={cardForm}
        setFormData={setCardForm}
        accounts={accounts}
        editCardId={editCardId}
        onSubmit={handleCardSubmit}
      />

      <ExpenseDialog
        open={showExpenseDialog}
        onOpenChange={(open) => {
          setShowExpenseDialog(open);
          if (!open) {
            setEditExpense(null);
            setExpenseForm(createDefaultExpenseForm());
          }
        }}
        formData={expenseForm}
        setFormData={setExpenseForm}
        card={selectedCard}
        categories={categories}
        editExpense={editExpense}
        onSubmit={handleExpenseSubmit}
        onDelete={(expense) => setDeleteExpense(expense)}
      />

      <InvoiceImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onParse={parseCardInvoicePdf}
        onImport={handleImportCharges}
        charges={selectedCharges}
        monthLabel={selectedMonthLabel}
      />

      <AlertDialog open={showClearInvoiceDialog} onOpenChange={setShowClearInvoiceDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar fatura selecionada?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove {selectedCharges.length} lançamento{selectedCharges.length === 1 ? "" : "s"} da fatura de <span className="capitalize">{selectedMonthLabel}</span> e recalcula o valor da fatura.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearInvoice} className="bg-destructive text-destructive-foreground">
              Limpar fatura
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteCardId} onOpenChange={() => setDeleteCardId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cartão?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove o cartão, mas não apaga transações já lançadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCard} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteExpense} onOpenChange={() => setDeleteExpense(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lançamento da fatura?</AlertDialogTitle>
            <AlertDialogDescription>
              A fatura será recalculada após excluir este lançamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteExpense} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreditCardTile({ card, account, transactions, isSelected, onSelect, onEdit, onDelete }) {
  const brand = getBrandInfo(card.brand);
  const cycle = getCardInvoiceCycle(card, new Date());
  const charges = getInvoiceCharges(transactions, card, cycle.invoiceKey);
  const invoice = getInvoiceTransaction(transactions, card, cycle.invoiceKey);
  const invoiceTotal = invoice ? parseCurrency(invoice.amount) : getInvoiceTotal(charges);
  const limit = parseCurrency(card.limit);
  const available = Math.max(0, limit - invoiceTotal);
  const usage = limit > 0 ? Math.min(100, Math.round((invoiceTotal / limit) * 100)) : 0;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className={`card-hover overflow-hidden transition-colors ${isSelected ? "border-primary ring-1 ring-primary/40" : ""}`}
      data-testid={`card-${card.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <BankIcon iconId={account?.bankIconId} bankName={account?.bank || card.accountName} size="md" />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${brand.color}`} />
                <h3 className="truncate font-semibold">{card.name}</h3>
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {account ? `${account.bank || account.name} • ${account.name}` : "Conta não vinculada"}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 gap-1" onClick={(event) => event.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} aria-label="Editar cartão">
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={onDelete} aria-label="Excluir cartão">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Fatura atual</span>
            <span className="font-mono font-bold text-destructive">{formatCurrency(invoiceTotal)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Vencimento</span>
            <span className="font-medium">{formatDate(cycle.dueDate)}</span>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
              <span>{formatCurrency(invoiceTotal)} de {formatCurrency(limit)}</span>
              <span>{usage}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary" style={{ width: `${usage}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">Limite disponível {formatCurrency(available)}</p>
          </div>
          {!account && (
            <Badge variant="destructive" className="w-fit">Vincule uma conta</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceChargesView({
  card,
  charges,
  expenseCategories = [],
  isLoading,
  monthLabel,
  searchTerm,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  categoryOptions,
  onPrevMonth,
  onNextMonth,
  onEditExpense,
  onDeleteExpense,
}) {
  if (!card) {
    return (
      <Card>
        <CardContent className="empty-state py-12">
          <ReceiptText className="h-10 w-10 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Selecione ou crie um cartão para ver a fatura</p>
        </CardContent>
      </Card>
    );
  }

  const columns = [
    {
      key: "date",
      label: "Data",
      className: "whitespace-nowrap",
      render: (charge) => formatDate(charge.date),
    },
    {
      key: "description",
      label: "Descrição",
      className: "min-w-0 px-1 sm:px-2",
      render: (charge) => {
        const isCredit = charge.amount_sign === "+";
        const Icon = isCredit ? ArrowUpRight : ArrowDownRight;

        return (
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <div className={cn("shrink-0 rounded-full p-1 sm:p-1.5", isCredit ? "bg-emerald-500/10" : "bg-destructive/10")}>
            <Icon className={cn("h-3.5 w-3.5", isCredit ? "text-emerald-600" : "text-destructive")} />
          </div>
          <div className="flex min-w-0 flex-col sm:flex-row sm:items-center sm:gap-2">
            <span className="max-w-[120px] truncate font-medium sm:max-w-[240px]">
              {cleanChargeDescription(charge.description)}
            </span>
            <ChargeRecurrenceBadge charge={charge} />
          </div>
        </div>
        );
      },
    },
    {
      key: "category",
      label: "Categoria",
      className: "hidden md:table-cell",
      render: (charge) => formatTransactionCategoryName(charge, expenseCategories),
    },
    {
      key: "amount",
      label: "Valor",
      align: "right",
      className: "w-[92px] px-1 sm:w-auto sm:px-2",
      render: (charge) => (
        <span className={cn("whitespace-nowrap font-mono text-xs font-semibold tabular-nums sm:text-sm", getInvoiceAmountClass(charge.amount_sign))}>
          {getInvoiceAmountLabel(charge.amount, charge.amount_sign)}
        </span>
      ),
    },
    {
      key: "installment",
      label: "Qtd",
      align: "center",
      className: "hidden md:table-cell",
      render: (charge) => (
        <span className="inline-flex items-center justify-center rounded-full bg-muted px-2 py-1 text-xs font-semibold text-muted-foreground">
          {getChargeInstallmentLabel(charge)}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Ações",
      align: "right",
      className: "w-[72px] px-1",
      render: (charge) => (
        <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
          <Button variant="ghost" size="sm" className="h-7 min-w-6 px-1" onClick={() => onEditExpense(charge)} aria-label="Editar lançamento">
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 min-w-6 px-1 text-muted-foreground hover:text-destructive" onClick={() => onDeleteExpense(charge)} aria-label="Excluir lançamento">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <section className="order-2 space-y-4 xl:order-1">
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={onPrevMonth} className="px-2 sm:px-3">
                <ChevronLeft className="h-4 w-4 sm:hidden" />
                <span className="hidden sm:inline">Anterior</span>
              </Button>
              <span className="font-semibold capitalize">{monthLabel}</span>
              <Button variant="outline" size="sm" onClick={onNextMonth} className="px-2 sm:px-3">
                <span className="hidden sm:inline">Próximo</span>
                <ChevronRight className="h-4 w-4 sm:hidden" />
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder="Buscar lançamentos..."
                  className="pl-9"
                />
              </div>
              <Select value={categoryFilter} onValueChange={onCategoryFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Categorias: Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Categorias: Todas</SelectItem>
                  {categoryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <MobileInvoiceChargesList
        charges={charges}
        expenseCategories={expenseCategories}
        isLoading={isLoading}
        onEditExpense={onEditExpense}
        onDeleteExpense={onDeleteExpense}
      />

      <Card className="hidden sm:block">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3, 4].map((item) => (
                <Skeleton key={item} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={charges}
              onRowClick={onEditExpense}
              emptyState="Você não possui lançamentos nessa fatura."
              className="overflow-hidden"
            />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function MobileInvoiceChargesList({ charges, expenseCategories = [], isLoading, onEditExpense, onDeleteExpense }) {
  if (isLoading) {
    return (
      <div className="space-y-3 sm:hidden">
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (!charges.length) {
    return (
      <div className="empty-state rounded-lg border bg-card py-8 sm:hidden">
        <CreditCard className="mb-3 h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Você não possui lançamentos nessa fatura.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:hidden">
      {charges.map((charge) => (
        <div
          key={charge.id}
          role="button"
          tabIndex={0}
          className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border bg-card p-3 text-left shadow-sm transition-colors hover:bg-muted/40"
          onClick={() => onEditExpense(charge)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onEditExpense(charge);
            }
          }}
        >
          <div className="min-w-0 space-y-1">
            <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="shrink-0">{formatDate(charge.date)}</span>
              <ReceiptText className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className={cn("truncate font-medium", charge.amount_sign === "+" ? "text-emerald-600" : "text-primary")}>
                {charge.amount_sign === "+" ? "Receita da fatura" : "Despesa da fatura"}
              </span>
            </div>
            <div className="truncate text-sm font-bold text-foreground">
              {cleanChargeDescription(charge.description)}
            </div>
            <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="truncate">{formatTransactionCategoryName(charge, expenseCategories)}</span>
              <ChargeRecurrenceBadge charge={charge} />
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            <span className={cn("whitespace-nowrap font-mono text-sm font-bold tabular-nums", getInvoiceAmountClass(charge.amount_sign))}>
              {getInvoiceAmountLabel(charge.amount, charge.amount_sign)}
            </span>
            <div className="flex items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
              <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                {getChargeInstallmentLabel(charge)}
              </span>
              <span className="text-base font-semibold text-muted-foreground">&gt;</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function InvoiceIconButton({ icon: Icon, label, variant = "outline", className, ...props }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size="icon"
          className={cn("h-10 w-full", className)}
          aria-label={label}
          title={label}
          {...props}
        >
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function InvoicePanel({
  card,
  account,
  cycle,
  invoice,
  invoiceTotal,
  chargeCount = 0,
  monthLabel,
  onAddExpense,
  onImportInvoice,
  onEditCard,
  onPayInvoice,
  onClearInvoice,
  onExportCsv,
  onExportPdf,
  onCreateChargeback,
}) {
  if (!card) {
    return (
      <Card>
        <CardContent className="empty-state py-12">
          <ReceiptText className="h-10 w-10 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Selecione ou crie um cartão para ver a fatura</p>
        </CardContent>
      </Card>
    );
  }

  const isPaid = invoice?.status === true;
  const available = Math.max(0, parseCurrency(card.limit) - invoiceTotal);

  return (
    <aside className="order-1 space-y-4 xl:order-2">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="flex min-w-0 items-center gap-2 text-lg">
              <ReceiptText className="h-5 w-5 text-primary" />
              <span className="truncate">Fatura</span>
            </CardTitle>
            <span className="rounded-full border border-primary px-3 py-1 text-sm font-semibold capitalize text-primary">
              {monthLabel}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            <SummaryItem icon={Wallet} label="Valor da fatura" value={formatCurrency(invoiceTotal)} />
            <SummaryItem icon={isPaid ? CheckCircle2 : Clock3} label="Status" value={invoiceTotal <= 0 ? "Fatura zerada" : isPaid ? "Fatura paga" : "Fatura aberta"} />
            <SummaryItem icon={CalendarDays} label="Fechamento" value={cycle ? formatDate(cycle.closingDate) : "-"} />
            <SummaryItem icon={CalendarDays} label="Vencimento" value={cycle ? formatDate(cycle.dueDate) : "-"} />
            <SummaryItem icon={CreditCard} label="Limite disponível" value={formatCurrency(available)} />
          </div>

          {!account && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              Este cartão precisa estar vinculado a uma conta para lançar movimentações e gerar fatura.
            </div>
          )}

          <TooltipProvider delayDuration={150}>
            <div className="grid grid-cols-4 gap-2">
              <InvoiceIconButton
                icon={Plus}
                label="Despesa"
                className="btn-glow"
                onClick={onAddExpense}
                disabled={!account}
              />
              <InvoiceIconButton
                icon={Upload}
                label="Importar"
                onClick={onImportInvoice}
                disabled={!account}
              />
              <InvoiceIconButton
                icon={CreditCard}
                label="Cartão"
                onClick={onEditCard}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-10 w-full"
                    aria-label="Mais opções"
                    title="Mais opções"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onSelect={onClearInvoice}
                    disabled={!chargeCount}
                    className="text-destructive focus:text-destructive"
                  >
                    <Eraser className="mr-2 h-4 w-4" />
                    Limpar Fatura
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onExportCsv} disabled={!chargeCount}>
                    <Download className="mr-2 h-4 w-4" />
                    Exportar CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onExportPdf} disabled={!chargeCount}>
                    <FileDown className="mr-2 h-4 w-4" />
                    Exportar PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onCreateChargeback} disabled={!account}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Lançar Extorno
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </TooltipProvider>

          <Button
            variant={isPaid ? "outline" : "default"}
            className="w-full"
            disabled={!invoice || invoiceTotal <= 0 || isPaid}
            onClick={() => onPayInvoice(invoice)}
          >
            {isPaid ? "Fatura paga" : "Pagar fatura"}
          </Button>
        </CardContent>
      </Card>
    </aside>
  );
}

function InvoiceImportDialog({ open, onOpenChange, onParse, onImport, charges = [], monthLabel }) {
  const [file, setFile] = useState(null);
  const [items, setItems] = useState([]);
  const [signOverrides, setSignOverrides] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const enrichedItems = useMemo(() => (
    items.map((item, index) => {
      const id = `${item.date}-${item.amount}-${index}`;
      const signedItem = {
        ...item,
        id,
        amount_sign: signOverrides[id] || item.amount_sign || "-",
      };

      return {
        ...signedItem,
        duplicate: isProbableImportedDuplicate(signedItem, charges),
      };
    })
  ), [charges, items, signOverrides]);

  const selectedItems = enrichedItems.filter((item) => selectedIds.has(item.id));
  const duplicateCount = enrichedItems.filter((item) => item.duplicate).length;
  const selectedTotal = selectedItems.reduce((sum, item) => sum + getImportedInvoiceAmount(item), 0);

  const reset = () => {
    setFile(null);
    setItems([]);
    setSignOverrides({});
    setSelectedIds(new Set());
    setIsParsing(false);
    setIsImporting(false);
  };

  const handleOpenChange = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setItems([]);
    setSignOverrides({});
    setSelectedIds(new Set());
  };

  const handleParse = async () => {
    if (!file) {
      toast.error("Selecione um PDF de fatura.");
      return;
    }

    setIsParsing(true);
    const result = await onParse(file);
    setIsParsing(false);

    if (!result.success) {
      toast.error(result.error || "Erro ao ler fatura.");
      return;
    }

    const nextItems = result.items || [];
    setItems(nextItems);
    setSignOverrides({});
    const nextSelected = new Set();
    nextItems.forEach((item, index) => {
      const id = `${item.date}-${item.amount}-${index}`;
      const signedItem = { ...item, amount_sign: item.amount_sign || "-" };
      if (!isProbableImportedDuplicate(signedItem, charges)) {
        nextSelected.add(id);
      }
    });
    setSelectedIds(nextSelected);
  };

  const toggleItem = (id, checked) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleItemSign = (item) => {
    setSignOverrides((current) => ({
      ...current,
      [item.id]: item.amount_sign === "+" ? "-" : "+",
    }));
  };

  const handleImport = async () => {
    if (!selectedItems.length) {
      toast.error("Selecione ao menos uma despesa para importar.");
      return;
    }

    setIsImporting(true);
    const result = await onImport(selectedItems);
    setIsImporting(false);

    if (result.success) {
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden p-4 sm:max-w-3xl sm:p-6">
        <DialogHeader>
          <DialogTitle>Importar fatura</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border p-4">
            <Label htmlFor="invoice-import-file">Arquivo PDF</Label>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input
                id="invoice-import-file"
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleFileChange}
                data-testid="invoice-import-file"
              />
              <Button type="button" onClick={handleParse} disabled={!file || isParsing}>
                <FileText className="mr-2 h-4 w-4" />
                {isParsing ? "Lendo..." : "Ler PDF"}
              </Button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              As despesas serão importadas para a fatura aberta de <span className="capitalize">{monthLabel}</span>.
            </p>
          </div>

          {enrichedItems.length > 0 && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <SummaryItem icon={ReceiptText} label="Encontradas" value={String(enrichedItems.length)} />
                <SummaryItem icon={Search} label="Duplicadas" value={String(duplicateCount)} />
                <SummaryItem icon={Wallet} label="Selecionado" value={formatCurrency(selectedTotal)} />
              </div>

              <div className="overflow-hidden rounded-lg border">
                <div className="grid grid-cols-[36px_92px_minmax(0,1fr)_110px] gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-semibold text-muted-foreground">
                  <span />
                  <span>Data</span>
                  <span>Descrição</span>
                  <span className="text-right">Valor</span>
                </div>
                <div className="max-h-[320px] overflow-y-auto">
                  {enrichedItems.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "grid grid-cols-[36px_92px_minmax(0,1fr)_110px] items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0",
                        item.duplicate && "bg-muted/30 text-muted-foreground"
                      )}
                    >
                      <Checkbox
                        checked={selectedIds.has(item.id)}
                        onCheckedChange={(checked) => toggleItem(item.id, checked === true)}
                      />
                      <span>{formatDate(`${item.date}T12:00:00`)}</span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{item.description}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.source_layout?.toUpperCase()}
                          {item.duplicate ? " • duplicado provável" : ""}
                        </span>
                      </span>
                      <button
                        type="button"
                        className={cn(
                          "whitespace-nowrap rounded px-1 py-0.5 text-right font-mono font-bold tabular-nums transition-colors hover:bg-muted",
                          getInvoiceAmountClass(item.amount_sign)
                        )}
                        onClick={() => toggleItemSign(item)}
                        title="Alternar positivo/negativo"
                        aria-label={`Alternar sinal de ${item.description}`}
                      >
                        {getInvoiceAmountLabel(item.amount, item.amount_sign)}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" className="flex-1 btn-glow" onClick={handleImport} disabled={!selectedItems.length || isImporting}>
              <Upload className="mr-2 h-4 w-4" />
              {isImporting ? "Importando..." : "Importar despesas"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-card p-3">
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 font-mono text-lg font-bold">{value}</p>
      </div>
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  );
}

function CardDialog({ open, onOpenChange, formData, setFormData, accounts, editCardId, onSubmit }) {
  const selectedAccount = accounts.find((account) => account.id === formData.accountId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editCardId ? "Editar Cartão" : "Novo Cartão"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Conta vinculada</Label>
            <Select value={formData.accountId} onValueChange={(value) => setFormData({ ...formData, accountId: value })}>
              <SelectTrigger data-testid="card-account">
                <SelectValue placeholder="Selecione a conta" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedAccount && (
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-2 text-sm">
                <BankIcon iconId={selectedAccount.bankIconId} bankName={selectedAccount.bank} size="sm" />
                <span className="truncate">{selectedAccount.bank || selectedAccount.name}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Nome do Cartão</Label>
            <Input
              placeholder="Ex: NuCard, Santander Elite..."
              value={formData.name}
              onChange={(event) => setFormData({ ...formData, name: event.target.value })}
              required
              data-testid="card-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Bandeira</Label>
              <Select value={formData.brand} onValueChange={(value) => setFormData({ ...formData, brand: value })}>
                <SelectTrigger data-testid="card-brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CARD_BRANDS.map((brand) => (
                    <SelectItem key={brand.value} value={brand.value}>
                      {brand.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Últimos 4 dígitos</Label>
              <Input
                maxLength={4}
                className="font-mono"
                placeholder="1234"
                value={formData.lastDigits}
                onChange={(event) => setFormData({ ...formData, lastDigits: event.target.value.replace(/\D/g, "") })}
                data-testid="card-digits"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Limite</Label>
            <CurrencyInput value={formData.limit} onChange={(value) => setFormData({ ...formData, limit: value })} data-testid="card-limit" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Dia do Fechamento</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={formData.closingDay}
                onChange={(event) => setFormData({ ...formData, closingDay: event.target.value })}
                data-testid="card-closing"
              />
            </div>

            <div className="space-y-2">
              <Label>Dia do Vencimento</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={formData.dueDay}
                onChange={(event) => setFormData({ ...formData, dueDay: event.target.value })}
                data-testid="card-due"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 btn-glow" data-testid="card-submit">
              {editCardId ? "Atualizar" : "Adicionar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseDialog({ open, onOpenChange, formData, setFormData, card, categories, editExpense, onSubmit, onDelete }) {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [repeatOpen, setRepeatOpen] = useState(false);
  const isDetailed = formData.cardChargeMode === "detailed";
  const isIncome = formData.cardChargeMode === "income";
  const chargeCategories = useMemo(() => (
    categories.filter((category) => category.type === (isIncome ? CATEGORY_TYPES.INCOME : CATEGORY_TYPES.EXPENSE))
  ), [categories, isIncome]);
  const selectedDate = useMemo(() => (
    formData.date ? new Date(formData.date) : new Date()
  ), [formData.date]);
  const invoiceOptions = useMemo(() => (
    card ? getCardInvoiceOptions(card, selectedDate, 12, 24) : []
  ), [card, selectedDate]);
  const installmentTotal = Math.max(2, parseInt(formData.installmentCount, 10) || 2);
  const installmentStart = Math.min(Math.max(1, parseInt(formData.installmentStartIndex, 10) || 1), installmentTotal);
  const installmentRemaining = installmentTotal - installmentStart + 1;
  const fixedTotal = Math.max(1, parseInt(formData.fixedTotalCount, 10) || 1);
  const detailTargetAmount = parseCurrency(formData.amount);
  const repeatSettings = {
    recurrenceType: formData.recurrenceType,
    recurrenceFrequency: formData.recurrenceFrequency,
    recurrenceInterval: formData.recurrenceInterval,
    installmentCount: formData.installmentCount,
    installmentStartIndex: formData.installmentStartIndex,
    amountMode: formData.amountMode,
    fixedTotalEnabled: formData.fixedTotalEnabled,
    fixedTotalCount: formData.fixedTotalCount,
  };
  const submitLabel = (() => {
    if (editExpense) return "Atualizar";
    if (formData.recurrenceType === "installment") return `Criar ${installmentRemaining} parcela${installmentRemaining > 1 ? "s" : ""}`;
    if (formData.recurrenceType === "fixed") {
      return formData.fixedTotalEnabled
        ? `Criar ${fixedTotal} ocorrência${fixedTotal > 1 ? "s" : ""}`
        : "Criar até Dezembro";
    }
    return "Adicionar";
  })();

  useEffect(() => {
    if (!open || !card || formData.invoiceManual) return;

    const automaticInvoiceKey = getCardInvoiceCycle(card, selectedDate).invoiceKey;
    if (formData.invoiceKey === automaticInvoiceKey) return;

    setFormData((current) => ({
      ...current,
      invoiceKey: automaticInvoiceKey,
    }));
  }, [open, card, selectedDate, formData.invoiceManual, formData.invoiceKey, setFormData]);

  const setField = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleDateChange = (date) => {
    if (!date || !card) return;
    const automaticInvoiceKey = getCardInvoiceCycle(card, date).invoiceKey;
    setFormData((current) => ({
      ...current,
      date,
      invoiceKey: current.invoiceManual ? current.invoiceKey : automaticInvoiceKey,
    }));
  };

  const handleChargeModeChange = (value) => {
    setFormData((current) => ({
      ...current,
      cardChargeMode: value,
      amountSign: getCardChargeModeSign(value),
      details: value === "detailed"
        ? (current.details?.length ? current.details : [createEmptyDetailItem()])
        : [],
      notes: value === "detailed" ? current.notes || "" : "",
      recurrenceType: value === "detailed" ? "none" : current.recurrenceType,
      ...(current.cardChargeMode === "income" || value === "income"
        ? { category: "", categoryId: null }
        : {}),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden p-4 sm:p-6",
        isDetailed ? "sm:max-w-2xl" : "sm:max-w-lg"
      )}>
        <DialogHeader>
          <DialogTitle>{editExpense ? "Editar Lançamento da Fatura" : "Novo Lançamento da Fatura"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo do lançamento</Label>
            <Select value={formData.cardChargeMode || "expense"} onValueChange={handleChargeModeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Despesa</SelectItem>
                <SelectItem value="income">Receita</SelectItem>
                <SelectItem value="detailed">Detalhado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Valor Total (R$)</Label>
            <NumericMask
              value={formData.amount}
              onChange={(value) => setField("amount", value)}
              placeholder="0,00"
              data-testid="card-expense-amount"
            />
          </div>

          <div className="rounded-lg border border-border/80 p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "h-[62px] w-full justify-start px-3 text-left font-normal",
                      selectedDate ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : "text-muted-foreground"
                    )}
                    data-testid="card-expense-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    <span className="truncate">
                      {selectedDate ? format(selectedDate, "PPP", { locale: ptBR }) : "Selecione a data"}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateChange}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>

              {isDetailed ? (
                <Button type="button" variant="outline" className="h-[62px] justify-start" disabled>
                  <ReceiptText className="mr-2 h-4 w-4" />
                  <span className="text-left">
                    <span className="block text-xs font-semibold uppercase text-muted-foreground">Detalhado</span>
                    <span className="block">Único</span>
                  </span>
                </Button>
              ) : (
                <RepeatTransactionButton
                  settings={repeatSettings}
                  onClick={() => setRepeatOpen(true)}
                  className="h-[62px]"
                />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Fatura</Label>
            <Select
              value={formData.invoiceKey}
              onValueChange={(value) => {
                setFormData((current) => ({
                  ...current,
                  invoiceKey: value,
                  invoiceManual: true,
                }));
              }}
            >
              <SelectTrigger data-testid="card-expense-invoice">
                <SelectValue placeholder="Selecione a fatura" />
              </SelectTrigger>
              <SelectContent>
                {invoiceOptions.map((option) => (
                  <SelectItem key={option.invoiceKey} value={option.invoiceKey}>
                    <span className="capitalize">{option.label}</span> - {option.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Input
              placeholder="Ex: Supermercado, Assinatura..."
              value={formData.description}
              onChange={(event) => setField("description", event.target.value.toLocaleUpperCase("pt-BR"))}
              required
              data-testid="card-expense-description"
            />
          </div>

          <TransactionCombobox
            label="Categoria"
            value={formData.category}
            setValue={(value) => setField("category", value)}
            selectedId={formData.categoryId}
            setSelectedId={(value) => setField("categoryId", value)}
            items={chargeCategories}
            open={categoryOpen}
            setOpen={setCategoryOpen}
            placeholder="Selecione ou digite uma categoria"
            testId="card-expense-category"
            showColor={true}
          />

          {isDetailed && (
            <>
              <DetailedItemsEditor
                items={formData.details || []}
                onChange={(items) => setField("details", items)}
                targetAmount={detailTargetAmount}
                categories={chargeCategories}
              />

              <div className="space-y-2">
                <Label htmlFor="card-expense-notes">Observação</Label>
                <Textarea
                  id="card-expense-notes"
                  value={formData.notes || ""}
                  onChange={(event) => setField("notes", event.target.value)}
                  maxLength={400}
                  placeholder="Observação"
                />
                <div className="flex justify-end text-xs text-muted-foreground">
                  {(formData.notes || "").length}/400
                </div>
              </div>
            </>
          )}

          {editExpense && (
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onDelete(editExpense)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir lançamento
            </Button>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 btn-glow">
              {submitLabel}
            </Button>
          </div>
        </form>
      </DialogContent>

      <RepeatTransactionDialog
        open={repeatOpen}
        onOpenChange={setRepeatOpen}
        amount={formData.amount}
        recurrenceType={formData.recurrenceType}
        setRecurrenceType={(value) => setField("recurrenceType", value)}
        recurrenceFrequency={formData.recurrenceFrequency}
        setRecurrenceFrequency={(value) => setField("recurrenceFrequency", value)}
        recurrenceInterval={formData.recurrenceInterval}
        setRecurrenceInterval={(value) => setField("recurrenceInterval", value)}
        installmentCount={formData.installmentCount}
        setInstallmentCount={(value) => setField("installmentCount", value)}
        installmentStartIndex={formData.installmentStartIndex}
        setInstallmentStartIndex={(value) => setField("installmentStartIndex", value)}
        amountMode={formData.amountMode}
        setAmountMode={(value) => setField("amountMode", value)}
        fixedTotalEnabled={formData.fixedTotalEnabled}
        setFixedTotalEnabled={(value) => setField("fixedTotalEnabled", value)}
        fixedTotalCount={formData.fixedTotalCount}
        setFixedTotalCount={(value) => setField("fixedTotalCount", value)}
      />
    </Dialog>
  );
}
